import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type AdminUiContextValue = {
  mobileSidebarOpen: boolean;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
};

const AdminUiContext = createContext<AdminUiContextValue | null>(null);

export const AdminUiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const toggleMobileSidebar = useCallback(() => {
    setMobileSidebarOpen((v) => !v);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setMobileSidebarOpen(false);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const onChange = () => {
      if (mq.matches) setMobileSidebarOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const value = useMemo(
    () => ({ mobileSidebarOpen, toggleMobileSidebar, closeMobileSidebar }),
    [mobileSidebarOpen, toggleMobileSidebar, closeMobileSidebar]
  );

  return <AdminUiContext.Provider value={value}>{children}</AdminUiContext.Provider>;
};

export function useAdminUi(): AdminUiContextValue {
  const ctx = useContext(AdminUiContext);
  if (!ctx) {
    throw new Error('useAdminUi must be used within AdminUiProvider');
  }
  return ctx;
}
